import React from 'react';
import classNames from 'classnames';
import { IExperiment } from '../../apiHooks/experimentApi.types';
import { ResultState } from '../../apiHooks/resultApi.types';
import { getResultStateFromExperiment } from '../../apiHooks/utils';

const stateToColor: Record<string, string> = {
    [ResultState.cancelled]: 'text-gray-400 bg-gray-400/10',
    [ResultState.completed]: 'text-teal-500 bg-teal-500/10',
    [ResultState.error]: 'text-red-400 bg-red-400/10',
    [ResultState.pending]: 'text-blue-400 bg-blue-400/10',
};

interface IProps {
    experiment: IExperiment;
    variant?: 'focus' | 'hover';
    showGoToHint?: boolean;
    className?: string;
}

export default function ExperimentListItemContent(props: IProps) {
    const { experiment, variant = 'hover', showGoToHint = false, className } = props;

    const lastCreatedResult = experiment.last_created_result;
    const state = getResultStateFromExperiment(experiment);

    const nameClasses = classNames(
        'font-semibold truncate',
        variant === 'focus' ? 'group-data-[focus]:text-white' : 'text-gray-900',
    );

    const subtitleClasses = classNames(
        'text-gray-600 truncate text-sm flex-1',
        variant === 'focus' && 'group-data-[focus]:text-indigo-100',
    );

    const timestampClasses = classNames(
        'flex-none text-right text-xs text-gray-600 ml-3 mb-0.5 self-end',
        variant === 'focus' && 'group-data-[focus]:text-indigo-100',
    );

    return (
        <>
            <div className='flex-shrink-0 mr-3'>
                {state === ResultState.pending ? (
                    <div className='flex-none rounded-full p-1'>
                        <span className='relative flex h-2 w-2'>
                            <span className='animate-ping absolute inline-flex h-full w-full rounded-full bg-sky-400 opacity-75' />
                            <span className='relative inline-flex rounded-full h-2 w-2 bg-sky-500' />
                        </span>
                    </div>
                ) : (
                    <div className={classNames(stateToColor[state], 'flex-none rounded-full p-1')}>
                        <div className='h-2 w-2 rounded-full bg-current' />
                    </div>
                )}
            </div>
            <div className={classNames('flex-1 min-w-0 flex flex-col justify-center', className)}>
                <p className={nameClasses}>{experiment.name ?? lastCreatedResult.name}</p>
                <div className='relative flex items-end'>
                    <p className={subtitleClasses}>
                        {experiment.name
                            ? `Experiment with ${experiment.result_counts.total} result${experiment.result_counts.total > 1 ? 's' : ''}`
                            : lastCreatedResult.app_uri}
                    </p>
                    <time className={timestampClasses} dateTime={new Date(experiment.created_at).toISOString()}>
                        {new Date(experiment.created_at).toLocaleDateString()}{' '}
                        {new Date(experiment.created_at).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                        })}
                    </time>
                    {showGoToHint && (
                        <p
                            className={classNames(
                                nameClasses,
                                'absolute right-0 bottom-full mb-0.5 hidden group-data-[focus]:block text-right pointer-events-none',
                            )}
                        >
                            Go to...
                        </p>
                    )}
                </div>
            </div>
        </>
    );
}
